import Gradient from '@ui/effects/Gradient'
import Border from '@ui/effects/Border'
import { colors } from '@ui/colors'
import {
  interpolate,
  SharedValue,
  useAnimatedStyle,
} from 'react-native-reanimated'
import useAnimatedToggle from '@ui/hooks/useAnimatedToggle'
import { BorderRadiuses, BorderWidths } from '@ui/effects/Border/types'
import {
  colorBackground,
  colorBorder,
  colorPressed,
  HighlightVariant,
} from './styles'
import { LinearGradientPoint } from 'expo-linear-gradient'

export type HighlightsLayers = Partial<Record<HighlightVariant, boolean>>

export type GradientDirection =
  | 'top-bottom'
  | 'bottom-top'
  | 'left-right'
  | 'right-left'

const defaultGradientDirection: GradientDirection = 'left-right'

export interface HightlightProps {
  variant: HighlightVariant
  animationPress?: SharedValue<number>
  borderWidths?: BorderWidths
  borderRadiuses?: BorderRadiuses
  layers?: HighlightsLayers
  gradientDirection?: GradientDirection
}

const gradientDirections: Record<
  GradientDirection,
  {
    start: LinearGradientPoint
    end: LinearGradientPoint
  }
> = {
  'top-bottom': { start: [0, 0.5], end: [1, 0.5] },
  'bottom-top': { start: [1, 0.5], end: [0, 0.5] },
  'left-right': { start: [0.5, 0], end: [0.5, 1] },
  'right-left': { start: [0.5, 1], end: [0.5, 0] },
}

const Highlight = (props: HightlightProps & { intent: HighlightVariant }) => {
  const {
    variant: active,
    intent: variant,
    borderWidths,
    borderRadiuses,
    gradientDirection = defaultGradientDirection,
  } = props

  const isActive = variant === active

  const animation = useAnimatedToggle(isActive)
  const animationActive = useAnimatedToggle(active === 'active')

  const style = useAnimatedStyle(() => ({
    opacity: interpolate(animation.value, [0, 1], [0, 1]),
  }))

  const hideDefault = useAnimatedStyle(() => ({
    opacity: interpolate(animationActive.value, [0, 1], [0, 1]),
  }))

  return (
    <>
      <Gradient
        colors={colorBackground[variant]}
        start={gradientDirections[gradientDirection].start}
        end={gradientDirections[gradientDirection].end}
        style={[style, borderRadiuses]}
      />
      <Border
        style={[
          style,
          {
            borderColor: colorBorder[variant],
          },
          borderWidths,
          borderRadiuses,
        ]}
      />
      <Gradient
        colors={[colors.white0, colors.white25, colors.white50]}
        start={gradientDirections[gradientDirection].start}
        end={gradientDirections[gradientDirection].end}
        style={[hideDefault, borderRadiuses]}
      />
    </>
  )
}

export default function Highlights(props: HightlightProps) {
  const stylePressed = useAnimatedStyle(() => ({
    opacity: interpolate(props.animationPress?.value || 0, [0, 1], [0, 1]),
  }))

  const gradientDirection = props.gradientDirection || defaultGradientDirection

  return (
    <>
      {props.layers?.disabled !== false && (
        <Highlight intent="disabled" {...props} />
      )}
      {props.layers?.default !== false && (
        <Highlight intent="default" {...props} />
      )}
      <Gradient
        colors={colorPressed[props.variant]}
        start={gradientDirections[gradientDirection].start}
        end={gradientDirections[gradientDirection].end}
        style={[stylePressed, props.borderRadiuses]}
      />
      {props.layers?.active !== false && (
        <Highlight intent="active" {...props} />
      )}
      {props.layers?.success !== false && (
        <Highlight intent="success" {...props} />
      )}
      {props.layers?.danger !== false && (
        <Highlight intent="danger" {...props} />
      )}
      {props.layers?.attention !== false && (
        <Highlight intent="attention" {...props} />
      )}
    </>
  )
}
